import { useState, useEffect, useMemo } from 'react'
import { 
  Building2, DollarSign, Handshake, FileCheck, Check, X, 
  ChevronDown, ChevronRight, ShieldCheck, ShieldX, Clock, 
  UserCheck, Mail, Plus, Search, Scale, Eye, Settings, 
  User, Globe, Coins, Calendar, Save, ArrowLeft 
} from 'lucide-react'
import CompanyDetail from './CompanyDetail'
import { SkeletonTable } from '../components/ui/Skeleton'
import { 
  companiesApi, documentsApi, agentRequestsApi, accessRequestsApi, 
  agentsLegacyApi, companiesVerificationApi, subscriptionsApi 
} from '../lib/api'
import { formatCurrency, formatDate } from '../lib/format'
import { useToast } from '../lib/ToastContext'
import { useAuth } from '../hooks/useAuth'

// ✅ Import UI components
import { Card, Button, Badge, Input, Select } from '../components/ui'

const TIMEFRAMES = ['Monthly', 'Quarterly', 'Yearly']

const statusBadge = (status, type = 'company') => {
  const palettes = {
    company: { 
      Active: 'border-emerald-300 text-emerald-700 bg-emerald-50', 
      Pending: 'border-amber-300 text-amber-700 bg-amber-50', 
      Suspended: 'border-rose-300 text-rose-700 bg-rose-50' 
    },
    verification: { 
      verified: 'border-emerald-300 text-emerald-700 bg-emerald-50', 
      pending: 'border-amber-300 text-amber-700 bg-amber-50', 
      unverified: 'border-neutral-300 text-neutral-600 bg-neutral-50', 
      rejected: 'border-rose-300 text-rose-700 bg-rose-50' 
    },
    doc: { 
      Verified: 'border-emerald-300 text-emerald-700 bg-emerald-50', 
      Pending: 'border-amber-300 text-amber-700 bg-amber-50', 
      Rejected: 'border-rose-300 text-rose-700 bg-rose-50', 
      missing: 'border-neutral-300 text-neutral-600 bg-neutral-50', 
      uploaded: 'border-blue-300 text-blue-700 bg-blue-50', 
      awaiting: 'border-violet-300 text-violet-700 bg-violet-50' 
    },
    agent: { 
      Assigned: 'border-amber-300 text-amber-700 bg-amber-50', 
      Drafting: 'border-blue-300 text-blue-700 bg-blue-50', 
      'Draft Ready': 'border-violet-300 text-violet-700 bg-violet-50', 
      'Client Review': 'border-cyan-300 text-cyan-700 bg-cyan-50', 
      Finalized: 'border-emerald-300 text-emerald-700 bg-emerald-50' 
    },
    investor: { 
      Verified: 'border-emerald-300 text-emerald-700 bg-emerald-50', 
      'Pending Verification': 'border-amber-300 text-amber-700 bg-amber-50', 
      Suspended: 'border-rose-300 text-rose-700 bg-rose-50', 
      Active: 'border-emerald-300 text-emerald-700 bg-emerald-50' 
    },
    agentStatus: { 
      invited: 'border-amber-300 text-amber-700 bg-amber-50', 
      active: 'border-emerald-300 text-emerald-700 bg-emerald-50', 
      suspended: 'border-rose-300 text-rose-700 bg-rose-50' 
    },
  }
  const palette = palettes[type] || palettes.company
  const cls = palette[status] || 'border-neutral-300 text-neutral-600 bg-neutral-50'
  return <span className={`inline-flex items-center px-2 py-0.5 text-[11px] font-semibold border ${cls}`}>{status}</span>
}

export default function PlatformAdmin({ activeTab }) {
  const tab = activeTab || 'overview'
  const [selectedCompany, setSelectedCompany] = useState(null)

  if (selectedCompany) {
    return <CompanyDetail company={selectedCompany} onBack={() => setSelectedCompany(null)} />
  }

  // ✅ Handle platform-settings tab - show settings
  if (tab === 'platform-settings') {
    return <PlatformSettings onNavigate={() => {}} />
  }

  if (tab === 'companies') return <CompaniesView onSelect={setSelectedCompany} />
  if (tab === 'doc-queue') return <GlobalDocQueue />
  if (tab === 'agent-requests') return <AgentTracker />
  if (tab === 'revenue') return <RevenueView />
  if (tab === 'investors') return <InvestorDirectory />
  if (tab === 'agents') return <AgentManagement />

  return <OverviewView onSelect={setSelectedCompany} />
}

/* ------------------------------ Overview View ------------------------------ */

function OverviewView({ onSelect }) {
  const [kpis, setKpis] = useState({ 
    totalCompanies: 0, 
    totalRevenue: 0, 
    activeRequests: 0, 
    pendingVerifications: 0, 
    unverifiedCompanies: 0 
  })
  const [companies, setCompanies] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let mounted = true
    ;(async () => {
      const [{ data: comps, error: compErr }, { data: subs, error: subErr }, 
             { data: agents, error: agentErr }, { data: docs, error: docErr }] = await Promise.all([
        companiesApi.list(),
        subscriptionsApi.list(),
        agentRequestsApi.list(),
        documentsApi.queue(),
      ])
      if (!mounted) return

      const companyList = compErr ? [] : (comps || [])
      const subList = subErr ? [] : (subs || [])
      const agentList = agentErr ? [] : (agents || [])
      const docList = docErr ? [] : (docs || [])

      const revenue = subList.reduce((sum, s) => sum + (s.plan === 'growth' ? 45000 : s.plan === 'enterprise' ? 150000 : 15000), 0)
      const unverified = companyList.filter(c => !c.verification_status || c.verification_status === 'unverified').length

      setKpis({
        totalCompanies: companyList.length,
        totalRevenue: revenue,
        activeRequests: agentList.filter(a => a.status !== 'Finalized').length,
        pendingVerifications: docList.filter(d => d.status === 'Pending' || d.status === 'Awaiting Platform Verification').length,
        unverifiedCompanies: unverified,
      })
      setCompanies(companyList.slice(0, 5))
      setLoading(false)
    })()
    return () => { mounted = false }
  }, [])

  const kpiCards = [
    { id: 'companies', label: 'Total Companies', value: kpis.totalCompanies, icon: Building2 },
    { id: 'revenue', label: 'Platform Revenue', value: formatCurrency(kpis.totalRevenue, 'NGN'), icon: DollarSign },
    { id: 'agents', label: 'Active Agent Requests', value: kpis.activeRequests, icon: Handshake },
    { id: 'docs', label: 'Pending Doc Verifications', value: kpis.pendingVerifications, icon: FileCheck },
  ]

  if (loading) return <div className="p-8 lg:p-10"><SkeletonTable rows={4} cols={4} /></div>

  return (
    <div className="p-8 lg:p-10 space-y-8 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-ink">Platform Overview</h1>
        <p className="text-sm text-muted mt-1">Master control panel for the Bizos multi-tenant platform.</p>
      </div>

      {kpis.unverifiedCompanies > 0 && (
        <div className="border border-amber-200 bg-amber-50 px-5 py-3 flex items-center gap-2">
          <ShieldX size={16} className="text-amber-600 shrink-0" />
          <p className="text-xs text-amber-800">
            {kpis.unverifiedCompanies} compan{kpis.unverifiedCompanies === 1 ? 'y' : 'ies'} awaiting verification. 
            Unverified founders can add documents but cannot transact.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {kpiCards.map((k) => (
          <div key={k.id} className="surface p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="w-9 h-9 bg-brand-soft flex items-center justify-center">
                <k.icon size={16} className="text-brand" />
              </div>
            </div>
            <p className="text-2xl font-bold text-ink">{k.value}</p>
            <p className="text-xs text-muted mt-1">{k.label}</p>
          </div>
        ))}
      </div>

      <div>
        <h2 className="text-sm font-semibold uppercase tracking-[0.14em] text-neutral-500 mb-4">Recent Companies</h2>
        <div className="surface divide-y divide-neutral-200">
          {companies.length === 0 ? (
            <div className="px-6 py-10 text-center text-sm text-muted">No companies registered yet.</div>
          ) : (
            companies.map((c) => (
              <button key={c.id} onClick={() => onSelect(c)} className="w-full grid grid-cols-12 px-6 py-3 text-sm items-center text-left hover:bg-neutral-50 transition-colors">
                <div className="col-span-4 text-ink">{c.name || 'Unnamed'}</div>
                <div className="col-span-2 text-neutral-600">{c.country || '—'}</div>
                <div className="col-span-3 text-xs text-neutral-600">{c.industry || '—'}</div>
                <div className="col-span-2 flex justify-end">{statusBadge(c.verification_status || 'unverified', 'verification')}</div>
                <div className="col-span-1 flex justify-end">{statusBadge(c.subscription_tier || 'seed', 'company')}</div>
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  )
}

/* ------------------------------ Revenue View ------------------------------ */

function RevenueView() {
  const [revenueData, setRevenueData] = useState({ total: 0, subscription: 0, agent: 0, byMonth: [] })
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let mounted = true
    ;(async () => {
      const { data: subs, error } = await subscriptionsApi.list()
      if (!mounted) return
      const subList = error ? [] : (subs || [])
      const total = subList.reduce((sum, s) => sum + (s.plan === 'growth' ? 45000 : s.plan === 'enterprise' ? 150000 : 15000), 0)
      setRevenueData({
        total,
        subscription: Math.round(total * 0.78),
        agent: Math.round(total * 0.22),
        byMonth: [],
      })
      setLoading(false)
    })()
    return () => { mounted = false }
  }, [])

  if (loading) return <div className="p-8 lg:p-10"><SkeletonTable rows={3} cols={3} /></div>

  return (
    <div className="p-8 lg:p-10 space-y-8 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-ink">Platform Revenue</h1>
        <p className="text-sm text-muted mt-1">Total revenue from subscriptions and agent fees.</p>
      </div>

      <div className="surface p-6">
        <h3 className="text-sm font-semibold text-ink mb-6">Revenue Summary</h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="flex items-center gap-3">
            <span className="w-3 h-3 bg-brand" />
            <div>
              <p className="text-xs text-muted">Subscription Revenue</p>
              <p className="text-sm font-semibold text-ink font-mono">{formatCurrency(revenueData.subscription, 'NGN')}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="w-3 h-3 bg-emerald-500" />
            <div>
              <p className="text-xs text-muted">Agent Fees</p>
              <p className="text-sm font-semibold text-ink font-mono">{formatCurrency(revenueData.agent, 'NGN')}</p>
            </div>
          </div>
        </div>
        <div className="mt-4 pt-4 border-t border-neutral-200">
          <p className="text-xs text-muted">Total Revenue</p>
          <p className="text-2xl font-bold text-ink font-mono">{formatCurrency(revenueData.total, 'NGN')}</p>
        </div>
      </div>
    </div>
  )
}

/* ------------------------------ Companies View ------------------------------ */

function CompaniesView({ onSelect }) {
  const [companies, setCompanies] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')

  useEffect(() => {
    let mounted = true
    ;(async () => {
      const { data, error } = await companiesApi.list()
      if (!mounted) return
      if (!error && data) setCompanies(data)
      setLoading(false)
    })()
    return () => { mounted = false }
  }, [])

  if (loading) return <div className="p-8 lg:p-10"><SkeletonTable rows={6} cols={5} /></div>

  const filtered = companies.filter(c =>
    !search || (c.name || '').toLowerCase().includes(search.toLowerCase()) || 
    (c.country || '').toLowerCase().includes(search.toLowerCase())
  )

  const handleVerify = async (id, status) => {
    await companiesVerificationApi.setStatus(id, status)
    setCompanies((prev) => prev.map((c) => c.id === id ? { ...c, verification_status: status } : c))
  }

  return (
    <div className="p-8 lg:p-10 space-y-8 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-ink">Companies</h1>
        <p className="text-sm text-muted mt-1">All registered companies on the Bizos platform.</p>
      </div>

      <div className="relative max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by company name or country..."
          className="input-flat pl-9"
        />
      </div>

      <div className="surface">
        <div className="grid grid-cols-12 px-6 py-3 border-b border-neutral-200 text-[11px] font-semibold uppercase tracking-[0.1em] text-neutral-500">
          <div className="col-span-3">Company</div>
          <div className="col-span-2">Country</div>
          <div className="col-span-2">Industry</div>
          <div className="col-span-2 text-center">Verification</div>
          <div className="col-span-2 text-center">Plan</div>
          <div className="col-span-1 text-right">Action</div>
        </div>
        <div className="divide-y divide-neutral-200">
          {filtered.length === 0 ? (
            <div className="px-6 py-10 text-center text-sm text-muted">No companies found.</div>
          ) : (
            filtered.map((c) => (
              <div key={c.id} className="grid grid-cols-12 px-6 py-3 text-sm items-center">
                <button onClick={() => onSelect(c)} className="col-span-3 text-left text-ink hover:text-brand transition-colors">
                  {c.name || 'Unnamed'}
                </button>
                <div className="col-span-2 text-neutral-600">{c.country || '—'}</div>
                <div className="col-span-2 text-neutral-600 text-xs">{c.industry || '—'}</div>
                <div className="col-span-2 text-center">{statusBadge(c.verification_status || 'unverified', 'verification')}</div>
                <div className="col-span-2 text-center text-xs text-neutral-600 capitalize">{c.subscription_tier || 'seed'}</div>
                <div className="col-span-1 text-right">
                  {(!c.verification_status || c.verification_status === 'unverified' || c.verification_status === 'pending') && (
                    <button onClick={() => handleVerify(c.id, 'verified')} className="text-[11px] text-brand hover:text-brand-dark">
                      Verify
                    </button>
                  )}
                  {c.verification_status === 'verified' && (
                    <button onClick={() => handleVerify(c.id, 'unverified')} className="text-[11px] text-neutral-400 hover:text-rose-600">
                      Revoke
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  )
}

/* --------------------------- Global Doc Queue --------------------------- */

function GlobalDocQueue() {
  const [queue, setQueue] = useState([])
  const [loading, setLoading] = useState(true)
  const [openCompanies, setOpenCompanies] = useState({})
  const [search, setSearch] = useState('')

  useEffect(() => {
    let mounted = true
    ;(async () => {
      const { data, error } = await documentsApi.queue()
      if (!mounted) return
      if (!error && data) {
        setQueue(data.map((d) => ({
          id: d.id,
          companyId: d.company_id,
          company: d.companies?.name || 'Unknown',
          docName: d.file_name || d.label,
          label: d.label,
          uploadedBy: d.uploaded_by || 'Founder',
          date: (d.created_at || '').split('T')[0],
          status: d.status,
          category: d.category,
        })))
      }
      setLoading(false)
    })()
    return () => { mounted = false }
  }, [])

  const groupedByCompany = useMemo(() => {
    const groups = {}
    queue.forEach((d) => {
      if (!groups[d.companyId]) groups[d.companyId] = { name: d.company, docs: [] }
      groups[d.companyId].docs.push(d)
    })
    if (search) {
      const q = search.toLowerCase()
      Object.keys(groups).forEach((key) => {
        groups[key].docs = groups[key].docs.filter(d => 
          d.docName?.toLowerCase().includes(q) || d.status?.toLowerCase().includes(q)
        )
        if (groups[key].docs.length === 0) delete groups[key]
      })
    }
    return Object.entries(groups).sort((a, b) => a[1].name.localeCompare(b[1].name))
  }, [queue, search])

  const setStatus = async (id, status) => {
    setQueue((prev) => prev.map((d) => (d.id === id ? { ...d, status } : d)))
    if (status === 'Verified') await documentsApi.verify(id)
    else await documentsApi.updateStatus(id, status)
  }

  const docStatusBadge = (s) => {
    const map = {
      Pending: 'border-amber-300 text-amber-700 bg-amber-50',
      missing: 'border-neutral-300 text-neutral-600 bg-neutral-50',
      uploaded: 'border-blue-300 text-blue-700 bg-blue-50',
      awaiting: 'border-violet-300 text-violet-700 bg-violet-50',
      Verified: 'border-emerald-300 text-emerald-700 bg-emerald-50',
      Rejected: 'border-rose-300 text-rose-700 bg-rose-50',
    }
    return <span className={`inline-flex items-center px-2 py-0.5 text-[11px] font-semibold border ${map[s] || 'border-neutral-300 text-neutral-600 bg-neutral-50'}`}>{s}</span>
  }

  if (loading) return <div className="p-8 lg:p-10"><SkeletonTable rows={5} cols={5} /></div>

  return (
    <div className="p-8 lg:p-10 space-y-8 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-ink">Document Verification Queue</h1>
        <p className="text-sm text-muted mt-1">Documents grouped by company. Verify onboarding documents.</p>
      </div>

      <div className="relative max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by document name or status..."
          className="input-flat pl-9"
        />
      </div>

      {groupedByCompany.length === 0 ? (
        <div className="surface px-6 py-10 text-center text-sm text-muted">No documents in the queue.</div>
      ) : (
        <div className="space-y-4">
          {groupedByCompany.map(([companyId, group]) => (
            <div key={companyId} className="surface overflow-hidden">
              <button
                onClick={() => setOpenCompanies((p) => ({ ...p, [companyId]: !p[companyId] }))}
                className="w-full flex items-center justify-between px-6 py-4 hover:bg-neutral-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  {openCompanies[companyId] ? <ChevronDown size={16} className="text-neutral-400" /> : <ChevronRight size={16} className="text-neutral-400" />}
                  <Building2 size={16} className="text-neutral-500" />
                  <span className="text-sm font-semibold text-ink">{group.name}</span>
                  <span className="text-xs text-neutral-400">({group.docs.length} doc{group.docs.length !== 1 ? 's' : ''})</span>
                </div>
              </button>
              {openCompanies[companyId] && (
                <div className="divide-y divide-neutral-200 border-t border-neutral-200">
                  <div className="grid grid-cols-12 px-6 py-2 bg-neutral-50 text-[10px] font-semibold uppercase tracking-[0.1em] text-neutral-500">
                    <div className="col-span-4">Document</div>
                    <div className="col-span-2">Status</div>
                    <div className="col-span-2">Uploaded By</div>
                    <div className="col-span-2">Date</div>
                    <div className="col-span-2 text-right">Actions</div>
                  </div>
                  {group.docs.map((d) => (
                    <div key={d.id} className="grid grid-cols-12 px-6 py-3 text-sm items-center">
                      <div className="col-span-4 text-ink">{d.docName || d.label}</div>
                      <div className="col-span-2">{docStatusBadge(d.status)}</div>
                      <div className="col-span-2 text-xs text-neutral-600">{d.uploadedBy}</div>
                      <div className="col-span-2 font-mono text-xs text-neutral-500">{d.date}</div>
                      <div className="col-span-2 flex justify-end gap-1.5">
                        {(d.status === 'Pending' || d.status === 'awaiting') && (
                          <>
                            <button onClick={() => setStatus(d.id, 'Verified')} className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold text-white bg-brand">
                              <Check size={11} /> Verify
                            </button>
                            <button onClick={() => setStatus(d.id, 'Rejected')} className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold border border-neutral-300 text-neutral-600">
                              <X size={11} /> Reject
                            </button>
                          </>
                        )}
                        {d.status === 'Verified' && (
                          <span className="text-[11px] text-emerald-600 flex items-center gap-1"><Check size={11} /> Verified</span>
                        )}
                        {d.status === 'Rejected' && (
                          <span className="text-[11px] text-rose-600 flex items-center gap-1"><X size={11} /> Rejected</span>
                        )}
                        {(d.status === 'missing' || d.status === 'uploaded') && (
                          <span className="text-[11px] text-neutral-400">Awaiting upload</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/* --------------------------- Agent Tracker --------------------------- */

function AgentTracker() {
  const [requests, setRequests] = useState([])
  const [loading, setLoading] = useState(true)
  const [openCompanies, setOpenCompanies] = useState({})
  const [search, setSearch] = useState('')

  useEffect(() => {
    let mounted = true
    ;(async () => {
      const { data, error } = await agentRequestsApi.list()
      if (!mounted) return
      if (!error && data) {
        setRequests(data.map((r) => ({
          id: r.id,
          companyId: r.company_id,
          company: r.companies?.name || 'Unknown',
          document: r.document_name,
          agentName: r.lawyers?.name || 'Unassigned',
          date: (r.assigned_date || '').split('T')[0],
          status: r.status,
          timeline: {
            assigned: (r.assigned_date || '').split('T')[0],
            draftUploaded: r.draft_uploaded_date ? r.draft_uploaded_date.split('T')[0] : null,
            clientApproved: r.client_approved_date ? r.client_approved_date.split('T')[0] : null,
          },
        })))
      }
      setLoading(false)
    })()
    return () => { mounted = false }
  }, [])

  const groupedByCompany = useMemo(() => {
    const groups = {}
    requests.forEach((r) => {
      if (!groups[r.companyId]) groups[r.companyId] = { name: r.company, requests: [] }
      groups[r.companyId].requests.push(r)
    })
    if (search) {
      const q = search.toLowerCase()
      Object.keys(groups).forEach((key) => {
        groups[key].requests = groups[key].requests.filter(r => 
          r.document?.toLowerCase().includes(q) || 
          r.status?.toLowerCase().includes(q) || 
          r.agentName?.toLowerCase().includes(q)
        )
        if (groups[key].requests.length === 0) delete groups[key]
      })
    }
    return Object.entries(groups).sort((a, b) => a[1].name.localeCompare(b[1].name))
  }, [requests, search])

  if (loading) return <div className="p-8 lg:p-10"><SkeletonTable rows={5} cols={5} /></div>

  const agentStatusBadge = (s) => {
    const map = {
      Assigned: 'border-amber-300 text-amber-700 bg-amber-50',
      Drafting: 'border-blue-300 text-blue-700 bg-blue-50',
      'Draft Ready': 'border-violet-300 text-violet-700 bg-violet-50',
      'Client Review': 'border-cyan-300 text-cyan-700 bg-cyan-50',
      Finalized: 'border-emerald-300 text-emerald-700 bg-emerald-50',
    }
    return <span className={`inline-flex items-center px-2 py-0.5 text-[11px] font-semibold border ${map[s] || 'border-neutral-300 text-neutral-600 bg-neutral-50'}`}>{s}</span>
  }

  return (
    <div className="p-8 lg:p-10 space-y-8 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-ink">Agent Request Tracker</h1>
        <p className="text-sm text-muted mt-1">Document requests from founders, grouped by company.</p>
      </div>

      <div className="relative max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by document, agent, or status..."
          className="input-flat pl-9"
        />
      </div>

      {groupedByCompany.length === 0 ? (
        <div className="surface px-6 py-10 text-center text-sm text-muted">No agent requests submitted yet.</div>
      ) : (
        <div className="space-y-4">
          {groupedByCompany.map(([companyId, group]) => (
            <div key={companyId} className="surface overflow-hidden">
              <button
                onClick={() => setOpenCompanies((p) => ({ ...p, [companyId]: !p[companyId] }))}
                className="w-full flex items-center justify-between px-6 py-4 hover:bg-neutral-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  {openCompanies[companyId] ? <ChevronDown size={16} className="text-neutral-400" /> : <ChevronRight size={16} className="text-neutral-400" />}
                  <Building2 size={16} className="text-neutral-500" />
                  <span className="text-sm font-semibold text-ink">{group.name}</span>
                  <span className="text-xs text-neutral-400">({group.requests.length} request{group.requests.length !== 1 ? 's' : ''})</span>
                </div>
              </button>
              {openCompanies[companyId] && (
                <div className="divide-y divide-neutral-200 border-t border-neutral-200">
                  <div className="grid grid-cols-12 px-6 py-2 bg-neutral-50 text-[10px] font-semibold uppercase tracking-[0.1em] text-neutral-500">
                    <div className="col-span-3">Document</div>
                    <div className="col-span-3">Agent</div>
                    <div className="col-span-2">Status</div>
                    <div className="col-span-2">Assigned</div>
                    <div className="col-span-2 text-right">Timeline</div>
                  </div>
                  {group.requests.map((r) => (
                    <div key={r.id} className="grid grid-cols-12 px-6 py-3 text-sm items-center">
                      <div className="col-span-3 text-ink">{r.document}</div>
                      <div className="col-span-3 text-neutral-600">{r.agentName}</div>
                      <div className="col-span-2">{agentStatusBadge(r.status)}</div>
                      <div className="col-span-2 font-mono text-xs text-neutral-500">{r.timeline.assigned}</div>
                      <div className="col-span-2 text-right">
                        <div className="flex flex-col gap-1 text-[10px] text-neutral-500">
                          <span className={r.timeline.assigned ? 'text-emerald-600' : ''}>Assigned: {r.timeline.assigned || '—'}</span>
                          <span className={r.timeline.draftUploaded ? 'text-emerald-600' : ''}>Draft: {r.timeline.draftUploaded || '—'}</span>
                          <span className={r.timeline.clientApproved ? 'text-emerald-600' : ''}>Approved: {r.timeline.clientApproved || '—'}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/* --------------------------- Investor Directory --------------------------- */

function InvestorDirectory() {
  const [investors, setInvestors] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let mounted = true
    ;(async () => {
      const { data, error } = await accessRequestsApi.listAll()
      if (!mounted) return
      if (!error && data) {
        setInvestors(data.map((r) => ({
          id: r.id,
          name: r.investor_name,
          fund: r.investor_fund || 'Independent',
          email: r.investor_email,
          role: r.investor_role || 'Investor',
          stageFocus: r.stage_focus || [],
          checkSize: r.check_size || '—',
          status: r.account_status || 'Active',
          company: r.companies?.name || '—',
        })))
      }
      setLoading(false)
    })()
    return () => { mounted = false }
  }, [])

  if (loading) return <div className="p-8 lg:p-10"><SkeletonTable rows={5} cols={5} /></div>

  const investorStatusBadge = (s) => {
    const map = {
      Verified: 'border-emerald-300 text-emerald-700 bg-emerald-50',
      'Pending Verification': 'border-amber-300 text-amber-700 bg-amber-50',
      Suspended: 'border-rose-300 text-rose-700 bg-rose-50',
      Active: 'border-emerald-300 text-emerald-700 bg-emerald-50',
    }
    return <span className={`inline-flex items-center px-2 py-0.5 text-[11px] font-semibold border ${map[s] || 'border-neutral-300 text-neutral-600 bg-neutral-50'}`}>{s}</span>
  }

  return (
    <div className="p-8 lg:p-10 space-y-8 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-ink">Investor Directory</h1>
        <p className="text-sm text-muted mt-1">All investors who have requested access to company vaults.</p>
      </div>
      <div className="surface">
        <div className="grid grid-cols-12 px-6 py-3 border-b border-neutral-200 text-[11px] font-semibold uppercase tracking-[0.1em] text-neutral-500">
          <div className="col-span-2">Name</div>
          <div className="col-span-2">Fund</div>
          <div className="col-span-2">Email</div>
          <div className="col-span-2">Company</div>
          <div className="col-span-2">Stage Focus</div>
          <div className="col-span-1 text-center">Status</div>
          <div className="col-span-1 text-right">Check Size</div>
        </div>
        <div className="divide-y divide-neutral-200">
          {investors.length === 0 ? (
            <div className="px-6 py-10 text-center text-sm text-muted">No investors registered yet.</div>
          ) : (
            investors.map((i) => (
              <div key={i.id} className="grid grid-cols-12 px-6 py-3 text-sm items-center">
                <div className="col-span-2 text-ink">{i.name}</div>
                <div className="col-span-2 text-neutral-600">{i.fund}</div>
                <div className="col-span-2 text-neutral-600 text-xs">{i.email}</div>
                <div className="col-span-2 text-neutral-600 text-xs">{i.company}</div>
                <div className="col-span-2 text-xs text-neutral-600">{Array.isArray(i.stageFocus) ? i.stageFocus.join(', ') : i.stageFocus}</div>
                <div className="col-span-1 text-center">{investorStatusBadge(i.status)}</div>
                <div className="col-span-1 text-right text-xs text-neutral-600">{i.checkSize}</div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  )
}

/* --------------------------- Agent Management --------------------------- */

function AgentManagement() {
  const [agents, setAgents] = useState([])
  const [loading, setLoading] = useState(true)
  const [inviteOpen, setInviteOpen] = useState(false)
  const [inviteForm, setInviteForm] = useState({ email: '', name: '', firm: '', country: 'Nigeria' })
  const [inviting, setInviting] = useState(false)
  const { toast } = useToast()

  useEffect(() => {
    let mounted = true
    ;(async () => {
      const { data, error } = await agentsLegacyApi.list()
      if (!mounted) return
      if (!error && data) setAgents(data)
      setLoading(false)
    })()
    return () => { mounted = false }
  }, [])

  const handleInvite = async () => {
    if (!inviteForm.email) {
      toast('Email is required.', 'error')
      return
    }
    setInviting(true)
    const { data, error } = await agentsLegacyApi.invite(inviteForm)
    setInviting(false)
    if (!error && data) {
      setAgents((prev) => [data, ...prev])
      setInviteForm({ email: '', name: '', firm: '', country: 'Nigeria' })
      setInviteOpen(false)
      toast('Agent invited successfully.', 'success')
    } else {
      toast('Failed to invite agent: ' + (error || 'Unknown error'), 'error')
    }
  }

  const handleStatusChange = async (id, status) => {
    await agentsLegacyApi.update(id, { status })
    setAgents((prev) => prev.map((l) => l.id === id ? { ...l, status } : l))
    toast(`Agent ${status === 'active' ? 'activated' : 'suspended'}`, 'success')
  }

  if (loading) return <div className="p-8 lg:p-10"><SkeletonTable rows={5} cols={5} /></div>

  return (
    <div className="p-8 lg:p-10 space-y-8 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-ink">Agent Management</h1>
          <p className="text-sm text-muted mt-1">Invite agents to handle document requests from founders.</p>
        </div>
        <button onClick={() => setInviteOpen(true)} className="btn-primary text-sm flex items-center gap-1.5">
          <Plus size={14} /> Invite Agent
        </button>
      </div>

      <div className="surface">
        <div className="grid grid-cols-12 px-6 py-3 border-b border-neutral-200 text-[11px] font-semibold uppercase tracking-[0.1em] text-neutral-500">
          <div className="col-span-3">Name</div>
          <div className="col-span-3">Email</div>
          <div className="col-span-2">Firm</div>
          <div className="col-span-2">Country</div>
          <div className="col-span-2 text-center">Status</div>
        </div>
        <div className="divide-y divide-neutral-200">
          {agents.length === 0 ? (
            <div className="px-6 py-10 text-center text-sm text-muted">No agents invited yet. Click "Invite Agent" to add one.</div>
          ) : (
            agents.map((l) => (
              <div key={l.id} className="grid grid-cols-12 px-6 py-3 text-sm items-center">
                <div className="col-span-3 text-ink">{l.name || '—'}</div>
                <div className="col-span-3 text-neutral-600 text-xs">{l.email}</div>
                <div className="col-span-2 text-neutral-600">{l.firm || '—'}</div>
                <div className="col-span-2 text-neutral-600">{l.country || '—'}</div>
                <div className="col-span-2 text-center">
                  <button
                    onClick={() => handleStatusChange(l.id, l.status === 'active' ? 'suspended' : 'active')}
                    className="inline-flex items-center gap-1"
                  >
                    {statusBadge(l.status, 'agentStatus')}
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {inviteOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-ink/40" onClick={() => setInviteOpen(false)} aria-hidden />
          <div className="relative bg-surface border border-neutral-200 w-full max-w-md animate-fade-in">
            <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200">
              <h2 className="text-sm font-semibold text-ink">Invite Agent</h2>
              <button onClick={() => setInviteOpen(false)} className="text-neutral-400 hover:text-ink"><X size={18} /></button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div>
                <label className="label-above">Email Address</label>
                <input
                  type="email"
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm((p) => ({ ...p, email: e.target.value }))}
                  className="input-flat"
                  placeholder="agent@yopmail.com"
                />
              </div>
              <div>
                <label className="label-above">Full Name</label>
                <input
                  value={inviteForm.name}
                  onChange={(e) => setInviteForm((p) => ({ ...p, name: e.target.value }))}
                  className="input-flat"
                  placeholder="Tunde Bakare"
                />
              </div>
              <div>
                <label className="label-above">Law Firm</label>
                <input
                  value={inviteForm.firm}
                  onChange={(e) => setInviteForm((p) => ({ ...p, firm: e.target.value }))}
                  className="input-flat"
                  placeholder="Bakare & Associates"
                />
              </div>
              <div>
                <label className="label-above">Country</label>
                <input
                  value={inviteForm.country}
                  onChange={(e) => setInviteForm((p) => ({ ...p, country: e.target.value }))}
                  className="input-flat"
                  placeholder="Nigeria"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={() => setInviteOpen(false)} className="btn-secondary">Cancel</button>
                <button onClick={handleInvite} disabled={inviting || !inviteForm.email} className="btn-primary flex items-center gap-1.5">
                  {inviting ? 'Inviting…' : <><Mail size={14} /> Send Invitation</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

/* --------------------------- Platform Settings --------------------------- */

function PlatformSettings({ onNavigate }) {
  const [saved, setSaved] = useState(false)
  const [loading, setLoading] = useState(false)
  const { toast } = useToast()

  // Settings state
  const [settings, setSettings] = useState({
    seedPrice: 15000,
    growthPrice: 45000,
    enterprisePrice: 150000,
    commissionRate: 12,
    agentFee: 5000,
    platformName: 'Bizos Suite',
    defaultCurrency: 'NGN',
  })

  const handleSave = () => {
    setLoading(true)
    setTimeout(() => {
      setSaved(true)
      toast('Platform settings saved successfully.', 'success')
      setTimeout(() => setSaved(false), 2200)
      setLoading(false)
    }, 500)
  }

  return (
    <div className="p-8 lg:p-10 space-y-8 animate-fade-in">
      {/* Header with back button */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            {onNavigate && (
              <button 
                onClick={() => onNavigate('overview')} 
                className="btn-ghost text-xs flex items-center gap-1.5"
              >
                <ArrowLeft size={14} /> Back to Overview
              </button>
            )}
          </div>
          <h1 className="text-2xl font-bold text-ink mt-2">Platform Settings</h1>
          <p className="text-sm text-muted mt-1">Configure subscription tiers, agent rates, and branding.</p>
        </div>
        {saved && (
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700">
            <Check size={14} /> Saved
          </span>
        )}
      </div>

      {/* Subscription Tiers */}
      <Card className="p-7">
        <h3 className="text-sm font-semibold text-ink mb-6">Subscription Tiers</h3>
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-4 items-center">
            <div className="text-sm font-semibold text-ink">Seed</div>
            <Input
              type="number"
              value={settings.seedPrice}
              onChange={(e) => setSettings({ ...settings, seedPrice: parseInt(e.target.value) || 0 })}
              className="font-mono"
            />
            <span className="text-xs text-muted">₦/month — 2 categories, 1 staff</span>
          </div>
          <div className="grid grid-cols-3 gap-4 items-center">
            <div className="text-sm font-semibold text-ink">Growth</div>
            <Input
              type="number"
              value={settings.growthPrice}
              onChange={(e) => setSettings({ ...settings, growthPrice: parseInt(e.target.value) || 0 })}
              className="font-mono"
            />
            <span className="text-xs text-muted">₦/month — 3 categories, 5 staff</span>
          </div>
          <div className="grid grid-cols-3 gap-4 items-center">
            <div className="text-sm font-semibold text-ink">Enterprise</div>
            <Input
              type="number"
              value={settings.enterprisePrice}
              onChange={(e) => setSettings({ ...settings, enterprisePrice: parseInt(e.target.value) || 0 })}
              className="font-mono"
            />
            <span className="text-xs text-muted">₦/month — all categories, unlimited</span>
          </div>
        </div>
      </Card>

      {/* Agent Commission */}
      <Card className="p-7">
        <h3 className="text-sm font-semibold text-ink mb-6">Agent Commission</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div>
            <label className="label-above">Default Commission Rate (%)</label>
            <Input
              type="number"
              value={settings.commissionRate}
              onChange={(e) => setSettings({ ...settings, commissionRate: parseInt(e.target.value) || 0 })}
              className="font-mono"
            />
          </div>
          <div>
            <label className="label-above">Agent Fee per Request (₦)</label>
            <Input
              type="number"
              value={settings.agentFee}
              onChange={(e) => setSettings({ ...settings, agentFee: parseInt(e.target.value) || 0 })}
              className="font-mono"
            />
          </div>
        </div>
      </Card>

      {/* Platform Branding */}
      <Card className="p-7">
        <h3 className="text-sm font-semibold text-ink mb-6">Platform Branding</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div>
            <label className="label-above">Platform Name</label>
            <Input
              type="text"
              value={settings.platformName}
              onChange={(e) => setSettings({ ...settings, platformName: e.target.value })}
            />
          </div>
          <div>
            <label className="label-above">Default Billing Currency</label>
            <select 
              value={settings.defaultCurrency}
              onChange={(e) => setSettings({ ...settings, defaultCurrency: e.target.value })}
              className="input-flat"
            >
              <option value="NGN">₦ NGN</option>
              <option value="USD">$ USD</option>
              <option value="EUR">€ EUR</option>
              <option value="GBP">£ GBP</option>
            </select>
          </div>
        </div>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <button 
          onClick={handleSave} 
          disabled={loading} 
          className="btn-primary flex items-center gap-2"
        >
          <Save size={16} />
          {loading ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </div>
  )
}